<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import Audit from '../../../components/Audit.vue'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useField, useForm } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import { object, string, array } from 'yup'
import {
  DBtn,
  DTab,
  InfoCircleIcon,
  DTabs,
  DLoading,
  DTextField,
  DMultiselect,
  DTextAreaField,
  DModal
} from 'front-library'
import {
  createRole,
  updateRole,
  getRole,
  getWorkspace
} from '@/app/core/services/roles'
usePayrollChange(() =>
  Boolean(route.params.id) ? back() : window.location.reload()
)

/* store, router, notifications */
const router = useRouter()
const route = useRoute()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos formulario */
const dataRole = ref({
  id: '',
  name: '',
  code: '',
  observation: '',
  permissionsIds: undefined
})
const viewRol = ref(route.name === 'GetRole')

/* Datos Listas */
const itemsPermissions = ref([] as any)
const itemsPermissionsAll = ref([] as any)

/* Datos componente Modal y Loading */
const showModal = ref(false)
const descriptionModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

const entity = ref('roles')

/* Esquema de validacion campos obligatorios */
const validationSchemaUpdate = object().shape({
  name: string()
    .required(t('form.valid.required'))
    .min(3, t('form.valid.minLength', [3]))
    .max(100, t('form.valid.maxLength', [100]))
    .matches(
      /^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/,
      t('form.valid.alphanumeric')
    ),
  observation: string()
    .required(t('form.valid.required'))
    .min(20, t('form.valid.minLength', [20]))
    .max(300, t('form.valid.maxLength', [300])),
  code: string()
    .required(t('form.valid.required'))
    .min(3, t('form.valid.minLength', [3]))
    .max(100, t('form.valid.maxLength', [100]))
    .matches(
      /^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/,
      t('form.valid.alphanumeric')
    ),
  permissionsIds: array().required(t('form.valid.required'))
})

const validationSchemaCreate = object().shape({
  name: string()
    .required(t('form.valid.required'))
    .min(3, t('form.valid.minLength', [3]))
    .max(100, t('form.valid.maxLength', [100]))
    .matches(
      /^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/,
      t('form.valid.alphanumeric')
    ),
  code: string()
    .required(t('form.valid.required'))
    .min(3, t('form.valid.minLength', [3]))
    .max(100, t('form.valid.maxLength', [100]))
    .matches(
      /^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/,
      t('form.valid.alphanumeric')
    ),
  permissionsIds: array().required(t('form.valid.required'))
})

/* Uso del metodo de validación, retorno de errores */
const { handleSubmit, errors } = useForm({
  initialValues: dataRole,
  validationSchema:
    route.params.id === undefined
      ? validationSchemaCreate
      : validationSchemaUpdate
})

/* Definicion de  campos del formulario */
const { value: name } = useField<string>('name')
const { value: observation } = useField<string>(ref('observation'))

const { value: code } = useField<string>('code')
const { value: permissionsIds } = useField<string[]>('permissionsIds')

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values, actions) => {
  void (route.params.id === undefined ? create(values) : update(values))
})

/* guardar rol */
const create = async (values: any) => {
  loading.value = true
  await createRole(values)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.created'),
        text: t('roles.notification.action', [t('notification.wasCreated')]),
        type: 'success',
        time: 10000
      })
      backConfirm()
    })
    .catch((error: any) => {
      notificationError(error, 'roles', t, notifications, loading)
    })
}

/* editar rol */
const update = async (values: any) => {
  loading.value = true
  await updateRole(dataRole.value.id.toString(), values)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.edited'),
        text: t('roles.notification.edit'),
        type: 'success',
        time: 12000
      })
      backConfirm()
    })
    .catch((error: any) => {
      notificationError(error, 'roles', t, notifications, loading)
    })
}

/* Abrir modal para reversar nomina */
const openModalConfirmation = (data: any) => {
  if (route.params.id === undefined) {
    onSubmit()
  } else {
    showModal.value = true
  }
}

/* Volver */
const back = () => {
  if (
    (name.value === '' &&
      code.value === '' &&
      ((permissionsIds.value as any)?.length === 0 ||
        permissionsIds.value === undefined)) ||
    route.params.id !== undefined
  ) {
    backConfirm()
  } else {
    showModal.value = true
  }
}
const backConfirm = () => {
  void router.push({ name: 'ListRoles' })
}
/* Metodo para cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  observation.value = ''
}

onMounted(async () => {
  loading.value = true
  try {
    itemsPermissions.value = await getWorkspace(store.user?.workspaceId)
    itemsPermissionsAll.value =
      itemsPermissions.value.availablePermissions.sort((a: any, b: any) =>
        a.name.localeCompare(b.name)
      )
    if (route.params.id !== undefined) {
      messageLoading.value = t('loading.data')
      const data = await getRole(route.params.id.toString())
      dataRole.value.id = data.id
      dataRole.value.name = data.name
      dataRole.value.code = data.code

      const permissions: any = []
      data.permissions.forEach((element: any) => {
        permissions.push(element.id)
      })
      dataRole.value.permissionsIds = permissions
    }
    loading.value = false
  } catch (error: any) {
    if (error.response.status === 400) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('roles.notification.error'),
        type: 'error',
        time: 10000
      })
      back()
    } else {
      notificationError(error, 'roles', t, notifications, loading)
    }
  }
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :description="descriptionModal"
    :isFooter="false"
    :size="'w-1/4 md:w-2/5 lg:w-1/3 xl:w-1/4 2xl:w-1/4'"
    @closeModal="closeModal">
    <template v-slot:form>
      <div class="flex flex-col items-center gap-10">
        <section
          class="flex items-center justify-center rounded-full sm:mx-0 h-14 w-14 bg-red-100">
          <InfoCircleIcon class="h-8 w-8 text-red-500" />
        </section>
        <section class="px-5 text-center text-gray-400 text-sm">
          <h1 class="text-black text-xl" v-html="t('roles.modal.title')" />
          <p class="mt-5" v-html="t('roles.modal.confirmationMessage')" />
          <DTextAreaField
            v-model="observation"
            :placeholder="t('roles.modal.reason')"
            :error="errors.observation"
            :rows="7" />
        </section>
        <section class="flex gap-3">
          <button
            @click="closeModal"
            class="hover:bg-gray-600 hover:text-white focus:ring-gray-200 inline-flex text-sm justify-center items-center py-1.5 px-10 border border-gray-300 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 font-bold"
            v-html="t('form.button.cancel')" />
          <button
            @click="onSubmit"
            class="bg-yellow-100 text-yellow-800 hover:bg-yellow-400 hover:text-white focus:ring-red-200 inline-flex text-sm justify-center items-center py-1.5 px-10 shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 font-bold"
            v-html="t('form.button.confirm')" />
        </section>
      </div>
    </template>
  </DModal>
  <div class="h-full px-5">
    <div class="bg-gray-50 pb-2 sm:pb-4">
      <div class="leading-4 pt-responsive">
        <p class="text-3xl font-semibold text-shadow text-blue-900">
          {{
            route.params.id == undefined
              ? t('roles.create')
              : viewRol
              ? t('roles.get')
              : t('roles.edit')
          }}
        </p>
      </div>
    </div>
    <form class="pt-responsive" @submit.prevent="openModalConfirmation">
      <DTabs>
        <template v-slot:default="slotParams">
          <DTab
            :title="t('tabs.data')"
            :selected-title="slotParams?.selectedTitle">
            <div class="grid grid-cols-8 px-6 py-4">
              <div
                class="col-span-8 sm:col-span-3 lg:col-span-2 text-base font-semibold text-gray-700 pb-2 sm:pb-0">
                {{ t('groups.basics.title') }}
              </div>
              <div
                class="col-span-8 sm:col-span-5 lg:col-span-6 grid grid-cols-6 gap-x-6 gap-y-4">
                <DTextField
                  class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                  :label="t('roles.fields.name.label')"
                  :error="errors.name"
                  :placeholder="t('roles.fields.name.placeholder')"
                  :required="true"
                  :disabled="viewRol"
                  v-model="name" />
                <DTextField
                  class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                  :label="t('roles.fields.code.label')"
                  :error="errors.code"
                  :placeholder="t('roles.fields.code.placeholder')"
                  :required="true"
                  :disabled="viewRol"
                  v-model="code" />
                <DMultiselect
                  class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                  :label="t('roles.fields.permissions.label')"
                  :error="errors.permissionsIds"
                  :required="true"
                  :disabled="viewRol"
                  v-model="permissionsIds"
                  :options="itemsPermissionsAll"
                  value-key="id"
                  :header-title="t('roles.fields.permissions.label')" />
              </div>
            </div>
            <div
              class="px-6 py-4 flex justify-end"
              v-if="route.params.id !== undefined">
              <p
                class="text-sm font-semibold text-blue-900"
                v-html="t('roles.notification.info')" />
            </div>
          </DTab>
        </template>
      </DTabs>
      <div class="p-5 bg-white shadow-md h-24 rounded-br-md rounded-bl-md">
        <hr class="pb-5" />
        <div
          class="flex items-center justify-between"
          :class="[
            store.hasPermission('audit:get') ? 'justify-between' : 'justify-end'
          ]">
          <Audit
            v-if="store.hasPermission('audit:get')"
            :id="dataRole.id.toString()"
            :form="entity" />
          <div>
            <DBtn
              type="button"
              class="font-semibold py-1 text-base mr-3"
              color="secondary"
              @click="back()">
              {{ $t('form.button.cancel') }}
            </DBtn>
            <DBtn class="font-semibold py-1 text-base">
              {{ t('form.button.save') }}
            </DBtn>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>
